import type { AppSnapshot, DeviceAddonConfig, Plan, VpnState } from "./types";

declare global {
  interface Window {
    Telegram?: {
      WebApp?: {
        initData?: string;
        ready?: () => void;
        expand?: () => void;
        openLink?: (value: string, options?: { try_instant_view?: boolean }) => void;
      };
    };
  }
}

export class ApiError extends Error {
  status: number;
  userMessage: string;

  constructor(status: number, userMessage: string) {
    super(userMessage);
    this.name = "ApiError";
    this.status = status;
    this.userMessage = userMessage;
  }
}

interface BootstrapResponse {
  account: {
    id: number;
    telegram_id: number;
    first_name: string;
    username: string | null;
    is_blocked: boolean;
  };
  vpn_state: VpnState;
  subscription: null | {
    id: number;
    status: string;
    expires_at: string;
    device_limit: number;
    subscription_url: string | null;
    happ_url: string | null;
    happ_direct_url: string | null;
    protocol: string;
    plan: null | {
      id: number;
      title: string;
      duration_days: number;
    };
    server: null | {
      id: number;
      code: string;
      title: string;
      country: string;
    };
  };
  days_left: number;
  plans: Array<{
    id: number;
    title: string;
    duration_days: number;
    price_rub: number;
    traffic_limit_gb: number | null;
  }>;
  device_addon: {
    price_per_device_rub: number;
    max_limit: number;
  };
}

export interface BootstrapData {
  snapshot: AppSnapshot;
  plans: Plan[];
  deviceAddon: DeviceAddonConfig;
}

export interface WebPublicInfo {
  support: {
    username: string;
    url: string | null;
  };
  instruction: {
    text: string;
    url: string | null;
  };
  about: string;
  legal: Array<{
    title: string;
    url: string;
  }>;
}

export interface WebReferralInfo {
  link: string;
  invited: number;
  qualified: number;
  pending_days: number;
  applied_days: number;
  total_days: number;
  signup_bonus_days: number;
  paid_bonus_days: number;
}

export interface WebBootstrapData extends BootstrapData {
  termsAccepted: boolean;
  publicInfo: WebPublicInfo;
}

interface WebMeResponse {
  user: {
    id: number;
    telegram_id: number;
    first_name: string;
    last_name: string | null;
    username: string | null;
  };
}

interface WebSubscriptionSummary {
  id: number;
  status: string;
  plan_id: number;
  plan: string;
  server_code: string;
  regions: string;
  protocols: string;
  expires_at: string;
  expires_label: string;
  remaining_days: number;
  traffic_limit_gb: number | null;
  device_limit: number;
  device_max: number;
  device_price_rub: number;
  can_add_device: boolean;
  has_connection: boolean;
}

interface WebAccountResponse {
  current_subscription: WebSubscriptionSummary | null;
  subscriptions: WebSubscriptionSummary[];
  terms_accepted: boolean;
  discount_percent: number;
}

interface WebPlansResponse {
  plans: Array<{
    id: number;
    title: string;
    duration_days: number;
    price_rub: string | number;
    price_stars: number | null;
    traffic_limit_gb: number | null;
  }>;
  payment_method: string | null;
  discount_percent: number;
  trial: {
    available: boolean;
    days: number;
    channel_url: string | null;
  };
  device_addon?: {
    price_per_device_rub: number;
    max_limit: number;
  };
}

interface WebVpnDetailResponse {
  subscription: WebSubscriptionSummary;
  subscription_url: string | null;
  saved_subscription_url?: string | null;
  happ_url: string | null;
  streisand_url: string | null;
}

interface WebOrderResponse {
  order: {
    id: number;
    status: "pending" | "paid" | "cancelled" | "failed";
    amount: string | number;
    currency: string;
    order_type: string;
    device_increment?: number | null;
    subscription_id?: number | null;
  };
  payment_url?: string;
}

interface WebPromoResponse {
  applied: boolean;
  type: string;
  value: number;
}

export interface CheckoutResponse {
  order_id: number;
  payment_url: string;
  amount: number;
  currency: string;
  kind: "subscription" | "device_addon";
  device_increment?: number;
  new_device_limit?: number;
}

export interface OrderStatusResponse {
  order_id: number;
  status: "pending" | "paid" | "cancelled" | "failed";
  kind: string;
  device_increment?: number | null;
}

export function getTelegramInitData(): string {
  return window.Telegram?.WebApp?.initData ?? "";
}

export function isTelegramMiniApp(): boolean {
  return Boolean(getTelegramInitData());
}

export function prepareTelegramWebApp(): void {
  window.Telegram?.WebApp?.ready?.();
  window.Telegram?.WebApp?.expand?.();
}

function formatDate(value: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

function mapBootstrap(payload: BootstrapResponse): BootstrapData {
  const subscription = payload.subscription;
  const plans: Plan[] = payload.plans.map((plan) => ({
    id: "plan-" + plan.id,
    backendId: plan.id,
    label: plan.title,
    price: plan.price_rub,
    days: plan.duration_days,
  }));

  return {
    snapshot: {
      account: {
        firstName: payload.account.first_name,
        username: payload.account.username,
        isBlocked: payload.account.is_blocked,
      },
      vpn: {
        state: payload.vpn_state,
        expiresAt: formatDate(subscription?.expires_at ?? null),
        daysLeft: payload.days_left,
        deviceLimit: subscription?.device_limit ?? 3,
        regions: subscription?.server?.title ? [subscription.server.title] : [],
        subscriptionUrl: subscription?.subscription_url ?? null,
        happUrl: subscription?.happ_url ?? null,
        happDirectUrl: subscription?.happ_direct_url ?? null,
        lastPlanLabel: subscription?.plan?.title ?? null,
        subscriptionId: subscription?.id ?? null,
      },
    },
    plans,
    deviceAddon: {
      pricePerDeviceRub: payload.device_addon.price_per_device_rub,
      maxLimit: payload.device_addon.max_limit,
    },
  };
}

export async function fetchBootstrap(): Promise<BootstrapData | null> {
  const initData = getTelegramInitData();
  if (!initData) return null;

  const response = await fetch("/api/miniapp/bootstrap", {
    method: "GET",
    headers: {
      "X-Telegram-Init-Data": initData,
      Accept: "application/json",
    },
    credentials: "same-origin",
    cache: "no-store",
  });

  if (!response.ok) {
    throw new ApiError(response.status, "Не удалось загрузить аккаунт");
  }

  return mapBootstrap((await response.json()) as BootstrapResponse);
}

function miniappHeaders(): HeadersInit {
  const initData = getTelegramInitData();
  return {
    Accept: "application/json",
    "Content-Type": "application/json",
    ...(initData ? { "X-Telegram-Init-Data": initData } : {}),
  };
}

async function miniappRequest<T>(url: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...miniappHeaders(),
      ...(options.headers ?? {}),
    },
    credentials: "same-origin",
    cache: "no-store",
  });

  if (!response.ok) {
    let reason = "Ошибка сервиса";
    try {
      const body = await response.json();
      reason = body.message ?? body.error ?? reason;
    } catch {
      // Non-JSON response.
    }
    throw new ApiError(response.status, reason);
  }

  return response.json() as Promise<T>;
}

function requestKey(): string {
  if (globalThis.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID().replaceAll("-", "");
  }
  return (
    Date.now().toString(36) +
    Math.random().toString(36).slice(2) +
    Math.random().toString(36).slice(2)
  ).slice(0, 48);
}

async function webRequest<T>(
  path: string,
  options: RequestInit = {},
  config: { idempotent?: boolean } = {},
): Promise<T> {
  const { idempotent = false } = config;
  const headers: HeadersInit = {
    Accept: "application/json",
    ...(options.body ? { "Content-Type": "application/json" } : {}),
    ...(idempotent ? { "Idempotency-Key": requestKey() } : {}),
    ...(options.headers ?? {}),
  };

  const response = await fetch("/api/web/" + path, {
    ...options,
    headers,
    credentials: "same-origin",
    cache: "no-store",
  });

  if (!response.ok) {
    let reason =
      response.status === 401
        ? "Нужно войти через Telegram"
        : "Сервис временно недоступен";
    try {
      const body = await response.json();
      reason = body.message ?? body.error ?? reason;
    } catch {
      // Non-JSON response.
    }
    throw new ApiError(response.status, reason);
  }

  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export async function fetchWebMe(): Promise<WebMeResponse["user"] | null> {
  try {
    return (await webRequest<WebMeResponse>("me")).user;
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return null;
    throw error;
  }
}

function mapWebState(subscription: WebSubscriptionSummary | null): VpnState {
  if (!subscription) return "never_had";
  if (subscription.status === "active" || subscription.status === "pending_activation") {
    return "active";
  }
  return "expired";
}

function mapWebPlans(payload: WebPlansResponse): Plan[] {
  return payload.plans.map((plan) => ({
    id: "plan-" + plan.id,
    backendId: plan.id,
    label: plan.title,
    price: Number(plan.price_rub),
    days: plan.duration_days,
  }));
}

export async function fetchWebBootstrap(): Promise<WebBootstrapData | null> {
  const me = await fetchWebMe();
  if (!me) return null;

  const [account, plansPayload, publicInfo] = await Promise.all([
    webRequest<WebAccountResponse>("account"),
    webRequest<WebPlansResponse>("plans"),
    webRequest<WebPublicInfo>("info"),
  ]);

  const current = account.current_subscription;
  let detail: WebVpnDetailResponse | null = null;
  if (account.terms_accepted && current) {
    detail = await webRequest<WebVpnDetailResponse>("vpns/" + current.id);
  }

  const subscriptionUrl =
    detail?.subscription_url ??
    detail?.saved_subscription_url ??
    null;

  return {
    snapshot: {
      account: {
        firstName: me.first_name || "Пользователь",
        username: me.username ? "@" + me.username.replace(/^@/, "") : null,
        isBlocked: false,
      },
      vpn: {
        state: mapWebState(current),
        expiresAt: current?.expires_label ?? null,
        daysLeft: current?.remaining_days ?? 0,
        deviceLimit: current?.device_limit ?? 3,
        regions: current?.regions ? [current.regions] : [],
        subscriptionUrl,
        happUrl: detail?.happ_url ?? null,
        happDirectUrl: null,
        lastPlanLabel: current?.plan ?? null,
        subscriptionId: current?.id ?? null,
      },
    },
    plans: mapWebPlans(plansPayload),
    deviceAddon: {
      pricePerDeviceRub:
        plansPayload.device_addon?.price_per_device_rub ??
        current?.device_price_rub ??
        50,
      maxLimit:
        plansPayload.device_addon?.max_limit ??
        current?.device_max ??
        5,
    },
    termsAccepted: account.terms_accepted,
    publicInfo,
  };
}

export function startTelegramWebLogin(): void {
  window.location.assign("/api/web/auth/telegram");
}

export async function logoutWebSession(): Promise<void> {
  await webRequest<void>("logout", { method: "POST" });
}

export async function acceptWebTerms(): Promise<void> {
  await webRequest<{ terms_accepted: boolean }>(
    "terms",
    {
      method: "POST",
      body: JSON.stringify({ accepted: true }),
    },
    { idempotent: true },
  );
}

export async function fetchWebReferral(): Promise<WebReferralInfo> {
  return webRequest<WebReferralInfo>("referral");
}

export async function applyWebPromocode(code: string): Promise<WebPromoResponse> {
  return webRequest<WebPromoResponse>(
    "promocode",
    {
      method: "POST",
      body: JSON.stringify({ code }),
    },
    { idempotent: true },
  );
}

export async function createSubscriptionCheckout(
  backendPlanId: number,
): Promise<CheckoutResponse> {
  if (isTelegramMiniApp()) {
    return miniappRequest<CheckoutResponse>("/api/miniapp/checkout/subscription", {
      method: "POST",
      body: JSON.stringify({ plan_id: backendPlanId }),
    });
  }

  const result = await webRequest<WebOrderResponse>(
    "orders",
    {
      method: "POST",
      body: JSON.stringify({ plan_id: backendPlanId }),
    },
    { idempotent: true },
  );

  if (!result.payment_url) {
    throw new ApiError(503, "Не удалось открыть оплату");
  }

  return {
    order_id: result.order.id,
    payment_url: result.payment_url,
    amount: Number(result.order.amount),
    currency: result.order.currency,
    kind: "subscription",
  };
}

export async function createDeviceAddonCheckout(
  quantity: number,
  subscriptionId?: number | null,
): Promise<CheckoutResponse> {
  if (isTelegramMiniApp()) {
    return miniappRequest<CheckoutResponse>("/api/miniapp/checkout/device-addon", {
      method: "POST",
      body: JSON.stringify({ quantity }),
    });
  }

  if (!subscriptionId) {
    throw new ApiError(400, "Активная подписка не найдена");
  }

  const result = await webRequest<WebOrderResponse>(
    "vpns/" + subscriptionId + "/devices",
    {
      method: "POST",
      body: JSON.stringify({ increment: quantity }),
    },
    { idempotent: true },
  );

  if (!result.payment_url) {
    throw new ApiError(503, "Не удалось открыть оплату");
  }

  return {
    order_id: result.order.id,
    payment_url: result.payment_url,
    amount: Number(result.order.amount),
    currency: result.order.currency,
    kind: "device_addon",
    device_increment: result.order.device_increment ?? quantity,
  };
}

export async function fetchOrderStatus(
  orderId: number,
): Promise<OrderStatusResponse> {
  if (isTelegramMiniApp()) {
    return miniappRequest<OrderStatusResponse>("/api/miniapp/orders/" + orderId);
  }

  const result = await webRequest<WebOrderResponse>("orders/" + orderId);
  return {
    order_id: result.order.id,
    status: result.order.status,
    kind: result.order.order_type,
    device_increment: result.order.device_increment ?? null,
  };
}

export function openExternalPayment(url: string): void {
  const webApp = window.Telegram?.WebApp;

  if (isTelegramMiniApp() && webApp?.openLink) {
    webApp.openLink(url);
    return;
  }

  window.open(url, "_blank", "noopener,noreferrer");
}

export function openExternalAppLink(url: string): void {
  const webApp = window.Telegram?.WebApp;

  if (isTelegramMiniApp() && webApp?.openLink && /^https?:\/\//i.test(url)) {
    webApp.openLink(url, { try_instant_view: false });
    return;
  }

  window.open(url, "_blank", "noopener,noreferrer");
}
